import React, { useState } from 'react'
import { withTranslation as translate } from 'react-i18next'
import QuoteContext from '../../components/Quote/context'
import withConfig from '../../services/withConfig'
import MyPageLink from './components/MyPageLink'
import WelcomeImage from './components/WelcomeImage'
import AdtractionScript from './components/AdtractionScript'
import API from '../../services/api'
import DocumentModal from './components/DocumentModal'
import TextButton from '../../components/TextButton'

// Documents come from memberships with status 'pending' or 'signed'
const isSigned = (doc) => doc.status === 'signed'

const Welcome = ({ t, config }) => {
  const token = sessionStorage.getItem('mypage-token')
  const [documents, setDocuments] = useState(JSON.parse(sessionStorage.getItem('documents') || '[]'))
  const [allSigned, setAllSigned] = useState(documents.every(isSigned))
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const cookieGet = (name) => {
    const cookies = document.cookie.split('; ')
    for (const cookie of cookies) {
      const [key, value] = cookie.split('=')
      if (key === name) {
        return decodeURIComponent(value)
      }
    }
  }

  const locale = sessionStorage.getItem('locale') === 'null' ? cookieGet('locale') : sessionStorage.getItem('locale')
  sessionStorage.clear()

  // Due to component being rendered multiple times we keep the token in session storage
  // to ensure the mypage button works correctly.
  //
  // The token will either expire or die with the browser session.
  sessionStorage.setItem('mypage-token', token)
  sessionStorage.setItem('locale', locale)
  sessionStorage.setItem('documents', JSON.stringify(documents))

  const handleSignClick = (doc) => {
    setSelectedDoc(doc)
    setIsModalOpen(true)
  }

  const closeModal = (signed) => {
    if (!signed) alert(t('signup.welcome.documents.unsigned'))

    setSelectedDoc(null)
    setIsModalOpen(false)
    fetchDocuments()
  }

  const hasDocuments = () => {
    return documents.length > 0
  }

  const featureEnabled = (name) => {
    return config.features?.includes(name) || false
  }

  const fetchDocuments = async () => {
    const params = {
      operator_id: documents[0].operatorId,
      membership_id: documents[0].membershipId
    }

    await new Promise((resolve, reject) => {
      API.fetchSigningDocuments(params, (data) => {
        if (data) {
          const fetched = data.documents || []
          // Keep the fetched documents, so a re-render or reload doesn't show them unsigned again
          sessionStorage.setItem('documents', JSON.stringify(fetched))
          if (fetched.every(isSigned)) {
            setAllSigned(true)
          }
          setDocuments(fetched)
        }
      },
      (err) => {
        console.error(err)
      })
    })
  }

  const sectionDocuments = (t) => {
    if (allSigned) return null
    return (
      <div>
        <h1>{t('signup.welcome.documents.pre_sign.title')}</h1>
        <h2>{t('signup.welcome.documents.pre_sign.read')}</h2>
        <p dangerouslySetInnerHTML={{ __html: t('signup.welcome.documents.pre_sign.details') }} />
        {documents.map((doc, i) => {
          return (
            <TextButton
              type='button'
              size='document-button'
              onClick={(e) => handleSignClick(doc)}
              text={`${doc.name} - ${t('signup.welcome.documents.sign')}`}
              key={`doc-${i}`}
            />
          )
        })}
      </div>
    )
  }

  const sectionAccess = (t, docs) => {
    return (
      <div>
        <div>
          <h1>{t('signup.welcome.header_top')}</h1>
          <h2 dangerouslySetInnerHTML={{ __html: t('signup.welcome.header_bottom') }} />
          {docs ? <p dangerouslySetInnerHTML={{ __html: t('signup.welcome.documents.post_sign.thanks') }} /> : null}
          <section id='sms'>
            <img src='/sms.svg' alt='' />
            <div>
              <p dangerouslySetInnerHTML={{ __html: t('signup.welcome.pin_code_info') }} />
              <p dangerouslySetInnerHTML={{ __html: t('signup.welcome.chip_info') }} />
            </div>
          </section>
        </div>
        <MyPageLink token={token} />
      </div>
    )
  }

  const chooseSection = (t) => {
    const docs = featureEnabled('doc-signing')

    if (docs && hasDocuments() && !allSigned) return sectionDocuments(t)

    return sectionAccess(t, docs)
  }

  return (
    <div className='welcome'>
      <section id='picture'>
        <WelcomeImage theme={config.theme} />
      </section>
      <main>
        {chooseSection(t)}
      </main>
      <QuoteContext.Consumer>
        {(quote) => <AdtractionScript config={config} quote={quote} />}
      </QuoteContext.Consumer>

      {isModalOpen && selectedDoc && (
        <DocumentModal
          doc={selectedDoc}
          onClose={closeModal}
          onError={closeModal}
        />
      )}
    </div>
  )
}

export default translate()(withConfig(Welcome))
